import { useState, useEffect } from "react";
import { Link } from "react-router";
import { ArrowLeft, Download } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import {
  fetchPressKitSections,
  fetchPressKitImages,
  fetchPressKitAssets,
  type PressKitSection,
  type PressKitImage,
  type PressKitAsset,
} from "./supabase";
import { useTheme } from "./ThemeInjector";
import { useLanguage } from "./LanguageContext";
import { localized } from "./admin/TranslationsPanel";
import { isSupabaseConnected } from "./supabase";

function simpleMarkdownToHtml(text: string): string {
  return text
    .replace(/\n\n+/g, "</p><p>")
    .replace(/\n/g, "<br/>")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/\[([^\]]+)\]\((mailto:[^)]+)\)/g, '<a href="$2">$1</a>')
    .replace(/^### (.+)$/gm, "<h3>$1</h3>")
    .replace(/^## (.+)$/gm, "<h2>$1</h2>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/^(.+)$/gm, (line) => (line.startsWith("<") ? line : "<p>" + line + "</p>"));
}

export function PressPage() {
  const theme = useTheme();
  const { t, lang } = useLanguage();
  const [sections, setSections] = useState<PressKitSection[]>([]);
  const [images, setImages] = useState<PressKitImage[]>([]);
  const [assets, setAssets] = useState<PressKitAsset[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConnected()) {
      setLoading(false);
      return;
    }
    Promise.all([
      fetchPressKitSections(),
      fetchPressKitImages(),
      fetchPressKitAssets(),
    ]).then(([s, i, a]) => {
      setSections(s.data ?? []);
      setImages(i.data ?? []);
      setAssets(a.data ?? []);
      setLoading(false);
    });
  }, []);

  const contentSections = sections.filter((s) => s.slug !== "press-contact");
  const pressContactSection = sections.find((s) => s.slug === "press-contact");

  if (loading) {
    return (
      <div className="pt-28 pb-24 px-6 max-w-3xl mx-auto text-center">
        <p style={{ fontFamily: theme.fontBody, fontSize: "15px", color: theme.muted }}>Loading…</p>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-24 px-6">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-2 mb-10 tracking-[0.15em] uppercase transition-opacity hover:opacity-80"
          style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.muted }}
        >
          <ArrowLeft size={14} /> Home
        </Link>

        {/* Hero + CTA */}
        <header className="text-center mb-16">
          <h1
            className="tracking-[0.06em] mb-4"
            style={{
              fontFamily: theme.fontHeading,
              fontSize: "clamp(28px, 4vw, 42px)",
              fontWeight: 400,
              color: theme.fg,
            }}
          >
            {t("nav.press")}
          </h1>
          <p
            className="mb-8 max-w-xl mx-auto"
            style={{
              fontFamily: theme.fontBody,
              fontSize: "15px",
              fontWeight: 300,
              color: theme.muted,
              lineHeight: 1.7,
            }}
          >
            Brand overview, imagery, and assets for media and partners.
          </p>
          {assets.length > 0 && (
            <a
              href={assets[0]?.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 border tracking-[0.2em] uppercase transition-colors"
              style={{
                fontFamily: theme.fontBody,
                fontSize: "11px",
                color: theme.primary,
                borderColor: theme.primary,
                borderRadius: theme.btnRadius,
              }}
            >
              <Download size={16} />
              Download Press Kit
            </a>
          )}
        </header>

        {/* Content sections */}
        {contentSections.map((section) => (
          <section key={section.id} className="mb-14">
            <h2
              className="tracking-[0.05em] mb-4"
              style={{
                fontFamily: theme.fontHeading,
                fontSize: "22px",
                fontWeight: 500,
                color: theme.fg,
              }}
            >
              {localized(section, "title", lang)}
            </h2>
            {(localized(section, "content", lang) || section.content) && (
              <div
                className="press-content [&>p]:mb-4 [&>h2]:mt-6 [&>h2]:mb-2 [&>h2]:text-lg [&>h3]:mt-4 [&>h3]:mb-2 [&>h3]:text-base [&>strong]:font-medium [&>a]:underline [&>a]:hover:opacity-80"
                style={{
                  fontFamily: theme.fontBody,
                  fontSize: "15px",
                  fontWeight: 300,
                  color: theme.fg,
                  lineHeight: 1.75,
                }}
                dangerouslySetInnerHTML={{ __html: simpleMarkdownToHtml(localized(section, "content", lang) || section.content || "") }}
              />
            )}
          </section>
        ))}

        {/* Image gallery */}
        {images.length > 0 && (
          <section className="mb-14">
            <h2
              className="tracking-[0.05em] mb-6"
              style={{
                fontFamily: theme.fontHeading,
                fontSize: "22px",
                fontWeight: 500,
                color: theme.fg,
              }}
            >
              Image Gallery
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {images.map((img) => (
                <div key={img.id} className="rounded-xl overflow-hidden bg-[#F2EEE8]">
                  <ImageWithFallback
                    src={img.image_url}
                    alt={img.alt_text || "Press image"}
                    className="w-full aspect-[4/3] object-cover"
                  />
                  {img.alt_text && (
                    <p
                      className="p-3 text-center"
                      style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.muted }}
                    >
                      {img.alt_text}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Logo / Downloads */}
        {assets.length > 0 && (
          <section className="mb-14">
            <h2
              className="tracking-[0.05em] mb-6"
              style={{
                fontFamily: theme.fontHeading,
                fontSize: "22px",
                fontWeight: 500,
                color: theme.fg,
              }}
            >
              Logo &amp; Downloads
            </h2>
            <div className="flex flex-wrap gap-4">
              {assets.map((asset) => (
                <a
                  key={asset.id}
                  href={asset.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 border rounded-lg tracking-[0.1em] uppercase transition-colors hover:opacity-80"
                  style={{
                    fontFamily: theme.fontBody,
                    fontSize: "12px",
                    color: theme.fg,
                    borderColor: theme.border,
                  }}
                >
                  <Download size={14} />
                  {asset.name}
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Press contact */}
        {pressContactSection && (
          <section className="pt-8 border-t" style={{ borderColor: theme.border }}>
            <h2
              className="tracking-[0.05em] mb-4"
              style={{
                fontFamily: theme.fontHeading,
                fontSize: "22px",
                fontWeight: 500,
                color: theme.fg,
              }}
            >
              {localized(pressContactSection, "title", lang)}
            </h2>
            {(localized(pressContactSection, "content", lang) || pressContactSection.content) && (
              <div
                className="press-content [&>p]:mb-3 [&>a]:underline [&>a]:hover:opacity-80"
                style={{
                  fontFamily: theme.fontBody,
                  fontSize: "15px",
                  fontWeight: 300,
                  color: theme.fg,
                  lineHeight: 1.75,
                }}
                dangerouslySetInnerHTML={{ __html: simpleMarkdownToHtml(localized(pressContactSection, "content", lang) || pressContactSection.content || "") }}
              />
            )}
          </section>
        )}
      </div>
    </div>
  );
}
